const TERMS_AGREEMENT_KEY = 'terms_agreement_status';
const TERMS_AGREEMENT_TIMESTAMP = 'terms_agreement_timestamp';

export interface TermsAgreementStatus {
  hasAgreed: boolean;
  timestamp: number;
  version: string;
  browserUserAgent?: string;
  browserLanguage?: string;
  browserPlatform?: string;
  consentData?: Record<string, unknown>;
}

const getBrowserInfo = () => {
  try {
    return {
      userAgent: navigator.userAgent || '',
      language: navigator.language || '',
      platform: navigator.platform || '',
      consentData: {
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack || 'unknown',
      },
    };
  } catch {
    return { userAgent: '', language: '', platform: '', consentData: {} };
  }
};

export const getTermsAgreementStatus = (): TermsAgreementStatus => {
  try {
    const stored = sessionStorage.getItem(TERMS_AGREEMENT_KEY);
    const timestamp = sessionStorage.getItem(TERMS_AGREEMENT_TIMESTAMP);
    const version = sessionStorage.getItem('terms_agreement_version') || '1.2';
    const userAgent = sessionStorage.getItem('terms_agreement_user_agent') || '';
    const language = sessionStorage.getItem('terms_agreement_language') || '';
    const platform = sessionStorage.getItem('terms_agreement_platform') || '';
    const consentData = sessionStorage.getItem('terms_agreement_consent_data');
    if (stored && timestamp) {
      const parsedTimestamp = parseInt(timestamp, 10);
      if (parsedTimestamp > 0 && !Number.isNaN(parsedTimestamp)) {
        return {
          hasAgreed: JSON.parse(stored),
          timestamp: parsedTimestamp,
          version,
          browserUserAgent: userAgent,
          browserLanguage: language,
          browserPlatform: platform,
          consentData: consentData ? JSON.parse(consentData) : {},
        };
      }
    }
  } catch {
    // ignore
  }
  return {
    hasAgreed: false,
    timestamp: 0,
    version: '1.2',
    browserUserAgent: '',
    browserLanguage: '',
    browserPlatform: '',
    consentData: {},
  };
};

export const setTermsAgreementStatus = (hasAgreed: boolean, version = '1.2'): void => {
  try {
    const browserInfo = getBrowserInfo();
    const timestamp = Date.now();
    sessionStorage.setItem(TERMS_AGREEMENT_KEY, JSON.stringify(hasAgreed));
    sessionStorage.setItem(TERMS_AGREEMENT_TIMESTAMP, timestamp.toString());
    sessionStorage.setItem('terms_agreement_version', version);
    sessionStorage.setItem('terms_agreement_user_agent', browserInfo.userAgent);
    sessionStorage.setItem('terms_agreement_language', browserInfo.language);
    sessionStorage.setItem('terms_agreement_platform', browserInfo.platform);
    sessionStorage.setItem('terms_agreement_consent_data', JSON.stringify(browserInfo.consentData));
  } catch {
    // ignore
  }
};

export const clearTermsAgreementStatus = (): void => {
  try {
    sessionStorage.removeItem(TERMS_AGREEMENT_KEY);
    sessionStorage.removeItem(TERMS_AGREEMENT_TIMESTAMP);
    sessionStorage.removeItem('terms_agreement_version');
    sessionStorage.removeItem('terms_agreement_user_agent');
    sessionStorage.removeItem('terms_agreement_language');
    sessionStorage.removeItem('terms_agreement_platform');
    sessionStorage.removeItem('terms_agreement_consent_data');
  } catch {
    // ignore
  }
};

export const hasUserAgreedToTerms = (): boolean => getTermsAgreementStatus().hasAgreed;

export const isTermsAgreementValid = (): boolean => {
  const status = getTermsAgreementStatus();
  return status.hasAgreed && status.timestamp > 0;
};
